import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { ACCESSED } from '../data/sources';
import { BRAND } from '../data/brand';
import { getSourceById, hostOf, lookupByUrl } from '../utils/sourceIndex';
import { Grade, GRADE_FOR_KIND, GRADE_INFO } from '../utils/grades';
interface SourceRefProps {
  id?: string;
  href?: string;
  label?: string;
  estimate?: {
    low: string;
    high: string;
  };
  note?: string;
  grade?: Grade;
  /** Optional short identifier for the figure, used in "Challenge this number". */
  figure?: string;
}
interface TipPos {
  x: number;
  y: number;
  below: boolean;
}
const markerClass = 'ml-[2px] inline-block rounded-[3px] px-[3px] font-mono text-[0.62rem] font-medium leading-none text-machine/90 align-super no-underline transition-colors duration-150 ease-out hover:bg-machine/15 hover:text-machine focus-visible:bg-machine/15';
export function SourceRef({
  id,
  href,
  label,
  estimate,
  note,
  grade,
  figure
}: SourceRefProps) {
  const ref = useRef<HTMLAnchorElement>(null);
  const hideTimer = useRef<number | null>(null);
  const [tip, setTip] = useState<TipPos | null>(null);
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    if (!tip) return;
    const close = () => setTip(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [tip]);
  useEffect(() => () => {
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
  }, []);
  const indexed = id ? getSourceById(id) : href ? lookupByUrl(href) : undefined;
  // When both id and href are given, keep the registry number but link to the specific (e.g. per-country) URL.
  const url = id && href ? href : indexed?.url ?? href;
  if (!url) return null;
  const name = indexed?.name ?? label ?? hostOf(url);
  const publisher = indexed?.publisher ?? hostOf(url);
  const marker = indexed ? String(indexed.n) : '↗';
  const internal = url.startsWith('/');
  const g: Grade = grade ?? (indexed ? GRADE_FOR_KIND[indexed.kind] : internal ? 'C' : 'B');
  const gInfo = GRADE_INFO[g];
  const aria = `Source ${marker}, confidence grade ${g} (${gInfo.label}): ${name}${estimate ? `. Estimate, range ${estimate.low} to ${estimate.high}` : ''}${internal ? '' : '. Opens in new tab'}`;
  const figId = `${figure ? `${figure} · ` : ''}S${marker}`;
  const mailto = `mailto:${BRAND.corrections}?subject=${encodeURIComponent(`Challenge: [${figId}] on ${BRAND.domain}${pathname}`)}&body=${encodeURIComponent(`Figure: ${figId}\nPage: ${BRAND.url}${pathname}\nSource cited: ${name} (${url})\n\nWhat looks wrong, and your better source:\n`)}`;
  const cancelHide = () => {
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    hideTimer.current = null;
  };
  const show = () => {
    cancelHide();
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const below = r.top < 190;
    const x = Math.min(window.innerWidth - 152, Math.max(152, r.left + r.width / 2));
    setTip({
      x,
      y: below ? r.bottom + 6 : r.top - 6,
      below
    });
  };
  const scheduleHide = () => {
    cancelHide();
    hideTimer.current = window.setTimeout(() => setTip(null), 140);
  };
  const handlers = {
    onMouseEnter: show,
    onMouseLeave: scheduleHide,
    onFocus: show,
    onBlur: scheduleHide
  };
  return <sup className="whitespace-nowrap leading-none" data-id="element-852" data-mp="5m5zb6b2bnma">
      {internal ? <Link ref={ref} to={url} className={markerClass} aria-label={aria} {...handlers} data-id="element-853" data-mp="3nzvs4ga2i031">
          {marker}
        </Link> : <a ref={ref} href={url} target="_blank" rel="noopener noreferrer" className={markerClass} aria-label={aria} {...handlers} data-id="element-854" data-mp="3s2ikcianhn0r">
          {marker}
        </a>}
      <span className={`ml-px font-mono text-[0.55rem] font-semibold ${gInfo.className}`} aria-hidden="true" title={`Grade ${g}: ${gInfo.label}`} data-id="element-855" data-mp="3j0j25mnwgcw8">
        {g}
      </span>
      {tip && createPortal(<div role="tooltip" onMouseEnter={cancelHide} onMouseLeave={scheduleHide} className="census-tip fixed z-[90] w-[296px] rounded-md border border-line bg-raised px-3 py-2.5 text-left shadow-2xl shadow-black/40" style={{
      left: tip.x,
      top: tip.y,
      transform: `translate(-50%, ${tip.below ? '0' : '-100%'})`
    }} data-id="element-856" data-mp="l6pjpia11wp1">
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted" data-id="element-857" data-mp="1ji1189uq555">
              [{marker}] {publisher}
            </p>
            <p className="mt-1 text-[13px] leading-snug text-ink" data-id="element-858" data-mp="3tnu21re8u5uk">{name}</p>
            <p className={`mt-1.5 font-mono text-[11px] ${gInfo.className}`} data-id="element-859" data-mp="3ooptn2z58c5a">
              Grade {g} · {gInfo.label}
            </p>
            {estimate && <p className="mt-1 font-mono text-[11px] text-machine" data-id="element-860" data-mp="1rtbk4lgx8mj5">
                Estimate · range {estimate.low}–{estimate.high}
              </p>}
            {note && <p className="mt-1 text-[12px] text-muted" data-id="element-861" data-mp="1ufu3ozntmb9l">{note}</p>}
            <div className="mt-2 flex items-center justify-between gap-3 border-t border-line pt-2" data-id="element-862" data-mp="1zef7ta671f3b">
              <span className="font-mono text-[10px] text-faint" data-id="element-863" data-mp="246ygpttxrm1s">{internal ? `${BRAND.domain} methodology` : `Accessed ${ACCESSED}`}</span>
              <a href={mailto} className="whitespace-nowrap text-[11.5px] text-ink underline decoration-line underline-offset-2 hover:decoration-machine" data-id="element-864" data-mp="2972z5peemqun">
                Challenge this number
              </a>
            </div>
          </div>, document.body)}
    </sup>;
}